import { useEffect, useRef } from 'react';

export interface LightboxImage {
  src: string;
  alt: string;
}

interface LightboxProps {
  images: LightboxImage[];
  index: number;
  onClose: () => void;
  onNavigate: (next: number) => void;
}

const Lightbox = ({ images, index, onClose, onNavigate }: LightboxProps) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);
  const current = images[index];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowRight' && index < images.length - 1) {
        e.preventDefault();
        onNavigate(index + 1);
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        onNavigate(index - 1);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [index, images.length, onClose, onNavigate]);

  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!current) return null;

  const onBackdropClick = (e: React.MouseEvent) => {
    if (e.target === dialogRef.current) onClose();
  };

  return (
    // The click handler only implements click-outside-to-dismiss, a mouse
    // convenience. Keyboard dismissal is already provided by the document
    // level Escape listener above, and the close button receives focus when
    // the dialog opens, so the accessible path does not rely on this handler.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions
    <div
      ref={dialogRef}
      className="gallery-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Aperçu de l’image"
      onClick={onBackdropClick}
    >
      <button
        ref={closeBtnRef}
        type="button"
        className="gallery-lightbox-close"
        aria-label="Fermer"
        onClick={onClose}
      >
        ✕
      </button>
      {index > 0 && (
        <button
          type="button"
          className="gallery-lightbox-nav gallery-lightbox-prev"
          aria-label="Image précédente"
          onClick={() => onNavigate(index - 1)}
        >
          ‹
        </button>
      )}
      <figure className="gallery-lightbox-figure">
        <img src={current.src} alt={current.alt} />
      </figure>
      {index < images.length - 1 && (
        <button
          type="button"
          className="gallery-lightbox-nav gallery-lightbox-next"
          aria-label="Image suivante"
          onClick={() => onNavigate(index + 1)}
        >
          ›
        </button>
      )}
    </div>
  );
};

export default Lightbox;
